import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import {
  CreditCard,
  Package,
  Server,
  Users,
  Clock,
  ArrowUpRight,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Loader2,
  Receipt,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const Subscription = () => {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [subRes, plansRes] = await Promise.all([
        fetch(`${API}/api/subscription`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/api/plans`),
      ]);
      const subData = await subRes.json();
      const plansData = await plansRes.json();
      setData(subData);
      setPlans(plansData);
    } catch (err) {
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async (planId) => {
    try {
      const res = await fetch(`${API}/api/checkout/session`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          plan_id: planId,
          billing_cycle: "monthly",
          origin_url: window.location.origin,
        }),
      });
      const result = await res.json();
      if (result.url) {
        window.location.href = result.url;
      } else {
        toast.error("Erreur lors de la creation de la session de paiement");
      }
    } catch (err) {
      toast.error("Erreur de connexion");
    }
  };

  const handleCancel = async () => {
    if (!window.confirm("Etes-vous sur de vouloir annuler votre abonnement ? Votre acces continuera jusqu'a la fin de la periode en cours.")) return;
    setCancelling(true);
    try {
      const res = await fetch(`${API}/api/subscription/cancel`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await res.json();
      if (res.ok) {
        toast.success(result.message);
        fetchData();
      } else {
        toast.error(result.detail || "Erreur");
      }
    } catch (err) {
      toast.error("Erreur de connexion");
    } finally {
      setCancelling(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin text-indigo-500" size={32} />
      </div>
    );
  }

  const { subscription, plan, usage, trial, payment_history } = data || {};
  const statusConfig = {
    active: { label: "Actif", color: "text-emerald-400", bg: "bg-emerald-500/10", icon: CheckCircle2 },
    trial: { label: "Essai", color: "text-amber-400", bg: "bg-amber-500/10", icon: Clock },
    cancelled: { label: "Annule", color: "text-red-400", bg: "bg-red-500/10", icon: XCircle },
    suspended: { label: "Suspendu", color: "text-red-400", bg: "bg-red-500/10", icon: AlertTriangle },
  };
  const subStatus = statusConfig[subscription?.status] || statusConfig.trial;
  const SubIcon = subStatus.icon;

  const serverPercent = usage?.max_servers > 0 ? Math.round((usage.servers / usage.max_servers) * 100) : 0;

  return (
    <div className="space-y-8" data-testid="subscription-page">
      <h1 className="text-2xl font-bold text-white">Mon Abonnement</h1>

      {/* Trial banner */}
      {trial?.active && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 flex items-center gap-3" data-testid="trial-banner">
          <Clock className="text-amber-400 shrink-0" size={24} />
          <div>
            <p className="text-amber-300 font-medium">Periode d'essai en cours</p>
            <p className="text-amber-400/70 text-sm">
              Il vous reste <span className="font-bold text-amber-300">{trial.days_left} jours</span> d'essai gratuit.
              {trial.ends_at && ` Expire le ${new Date(trial.ends_at).toLocaleDateString("fr-FR")}.`}
            </p>
          </div>
        </div>
      )}

      {/* Current plan card */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-slate-800/50 border border-slate-700/50 rounded-xl p-6" data-testid="current-plan-card">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-indigo-600/20 flex items-center justify-center">
                <Package className="text-indigo-400" size={24} />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">{plan?.name || "Aucun plan"}</h2>
                <p className="text-slate-400 text-sm">{plan?.description || "Choisissez un plan pour demarrer"}</p>
              </div>
            </div>
            <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium ${subStatus.bg} ${subStatus.color}`}>
              <SubIcon size={14} />
              {subStatus.label}
            </span>
          </div>

          {plan && (
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="bg-slate-900/50 rounded-lg p-4">
                <p className="text-slate-500 text-xs uppercase tracking-wider mb-1">Prix mensuel</p>
                <p className="text-2xl font-bold text-white">{plan.price_monthly}<span className="text-sm text-slate-400 font-normal"> EUR/mois</span></p>
              </div>
              <div className="bg-slate-900/50 rounded-lg p-4">
                <p className="text-slate-500 text-xs uppercase tracking-wider mb-1">Periode en cours</p>
                <p className="text-sm text-slate-300">
                  {subscription?.current_period_end
                    ? `Jusqu'au ${new Date(subscription.current_period_end).toLocaleDateString("fr-FR")}`
                    : "N/A"}
                </p>
              </div>
            </div>
          )}

          {/* Usage */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-slate-400 uppercase tracking-wider">Utilisation</h3>
            <div className="space-y-3">
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-sm text-slate-300 flex items-center gap-2">
                    <Server size={14} className="text-slate-500" /> Serveurs
                  </span>
                  <span className="text-sm font-mono text-slate-400">
                    {usage?.servers || 0} / {usage?.max_servers || "illimite"}
                  </span>
                </div>
                {usage?.max_servers > 0 && (
                  <div className="w-full bg-slate-700 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all ${serverPercent > 90 ? "bg-red-500" : serverPercent > 70 ? "bg-amber-500" : "bg-indigo-500"}`}
                      style={{ width: `${Math.min(serverPercent, 100)}%` }}
                      data-testid="server-usage-bar"
                    />
                  </div>
                )}
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-300 flex items-center gap-2">
                  <Users size={14} className="text-slate-500" /> Clients
                </span>
                <span className="text-sm font-mono text-slate-400">{usage?.clients || 0}</span>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 mt-6 pt-6 border-t border-slate-700/50">
            {subscription?.status === "active" && (
              <Button
                variant="outline"
                className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                onClick={handleCancel}
                disabled={cancelling}
                data-testid="cancel-subscription-btn"
              >
                {cancelling ? <Loader2 className="animate-spin mr-2" size={16} /> : <XCircle size={16} className="mr-2" />}
                Annuler l'abonnement
              </Button>
            )}
          </div>
        </div>

        {/* Quick upgrade */}
        <div className="space-y-4" data-testid="upgrade-plans">
          <h3 className="text-sm font-medium text-slate-400 uppercase tracking-wider">Changer de plan</h3>
          {plans.map((p) => {
            const isCurrent = plan?.id === p.id;
            return (
              <div
                key={p.id}
                className={`bg-slate-800/50 border rounded-lg p-4 transition-all ${
                  isCurrent ? "border-indigo-500/50 ring-1 ring-indigo-500/20" : "border-slate-700/50 hover:border-slate-600"
                }`}
                data-testid={`plan-option-${p.name.toLowerCase()}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium text-white">{p.name}</h4>
                  <span className="text-indigo-400 font-bold">{p.price_monthly} EUR</span>
                </div>
                <p className="text-xs text-slate-500 mb-3">{p.max_servers} serveurs max</p>
                {isCurrent ? (
                  <span className="text-xs text-indigo-400 font-medium">Plan actuel</span>
                ) : (
                  <Button
                    size="sm"
                    className="w-full bg-indigo-600 hover:bg-indigo-700"
                    onClick={() => handleUpgrade(p.id)}
                    data-testid={`upgrade-btn-${p.name.toLowerCase()}`}
                  >
                    <ArrowUpRight size={14} className="mr-1" />
                    {plan && p.price_monthly > plan.price_monthly ? "Upgrader" : "Changer"}
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Payment history */}
      {payment_history && payment_history.length > 0 && (
        <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6" data-testid="payment-history">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Receipt size={20} className="text-slate-400" />
            Historique des paiements
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-900/50">
                <tr>
                  <th className="text-left p-3 text-xs font-medium text-slate-500 uppercase">Date</th>
                  <th className="text-left p-3 text-xs font-medium text-slate-500 uppercase">Montant</th>
                  <th className="text-left p-3 text-xs font-medium text-slate-500 uppercase">Cycle</th>
                  <th className="text-left p-3 text-xs font-medium text-slate-500 uppercase">Statut</th>
                </tr>
              </thead>
              <tbody>
                {payment_history.map((tx) => (
                  <tr key={tx.id} className="border-t border-slate-800">
                    <td className="p-3 text-sm text-slate-300">{new Date(tx.created_at).toLocaleDateString("fr-FR")}</td>
                    <td className="p-3 text-sm font-mono text-white">{tx.amount} {tx.currency?.toUpperCase()}</td>
                    <td className="p-3 text-sm text-slate-400 capitalize">{tx.billing_cycle}</td>
                    <td className="p-3">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        tx.status === "paid" ? "bg-emerald-500/10 text-emerald-400" : "bg-amber-500/10 text-amber-400"
                      }`}>
                        {tx.status === "paid" ? "Paye" : "En attente"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default Subscription;
